'use client';

import { c as _c } from "react/compiler-runtime";
import type { FilterItem } from '@morpho-org/ui-v6/client';
import { computeActiveCount, MasterFilter } from '@morpho-org/ui-v6/client';
import type { AssetItem } from '@/components/common/TableAdvancedFilters/AssetFilter';
import { AssetFilter } from '@/helpers/filter/table';
import { BASE_FILTER_DEFINITIONS, PT_FILTER_DEFINITION, STOCKS_FILTER_DEFINITION } from '@/helpers/filter/vaultMasterFilter/assetFilter';
import { useAssetTagSlot } from '@/helpers/filter/vaultMasterFilter/useAssetTagSlot';
import { useMasterFilterTracking } from '@/lib/analytics/client/hooks/useMasterFilterTracking';
import type { VaultsMasterFilterState } from '../hooks/useVaultsMasterFilter';
import { DEFAULT_MASTER_FILTER_STATE, VAULTS_ANALYTICS_EXCLUDE_KEYS, VAULTS_MASTER_FILTER_EXCLUDE_KEYS } from '../hooks/useVaultsMasterFilter';
interface VaultsMasterFilterProps {
  value: VaultsMasterFilterState;
  onChange: (value: VaultsMasterFilterState) => void;
  allCollateralExposureAssets: AssetItem[];
  allCollateralExclusionAssets: AssetItem[];
  collateralExposureItems: FilterItem<string>[];
  collateralExclusionItems: FilterItem<string>[];
  disabled?: boolean;
}
export const VaultsMasterFilter = t0 => {
  const $ = _c(47);
  const {
    value,
    onChange,
    allCollateralExposureAssets,
    allCollateralExclusionAssets,
    collateralExposureItems,
    collateralExclusionItems,
    disabled
  } = t0;
  const tracking = useMasterFilterTracking("vaults");
  const buildFilterDefinitions = _temp3;
  let t1;
  if ($[0] !== allCollateralExposureAssets) {
    t1 = buildFilterDefinitions(allCollateralExposureAssets);
    $[0] = allCollateralExposureAssets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const exposureFilterDefinitions = t1;
  let t2;
  if ($[2] !== allCollateralExclusionAssets) {
    t2 = buildFilterDefinitions(allCollateralExclusionAssets);
    $[2] = allCollateralExclusionAssets;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const exclusionFilterDefinitions = t2;
  let t3;
  if ($[4] !== tracking) {
    t3 = (name, nextValue) => {
      tracking.onFieldChange(name, computeActiveCount(nextValue, DEFAULT_MASTER_FILTER_STATE, VAULTS_ANALYTICS_EXCLUDE_KEYS));
    };
    $[4] = tracking;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const trackTagChange = t3;
  let t4;
  if ($[6] !== onChange || $[7] !== trackTagChange || $[8] !== value) {
    t4 = assetIds => {
      const nextValue_0 = {
        ...value,
        collateralExposure: assetIds
      };
      trackTagChange("collateralExposure", nextValue_0);
      onChange(nextValue_0);
    };
    $[6] = onChange;
    $[7] = trackTagChange;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== allCollateralExposureAssets || $[11] !== exposureFilterDefinitions || $[12] !== t4 || $[13] !== value.collateralExposure) {
    t5 = {
      assets: allCollateralExposureAssets,
      selectedValues: value.collateralExposure,
      filterDefinitions: exposureFilterDefinitions,
      onSelect: t4
    };
    $[10] = allCollateralExposureAssets;
    $[11] = exposureFilterDefinitions;
    $[12] = t4;
    $[13] = value.collateralExposure;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const exposureTagSlot = useAssetTagSlot(t5);
  let t6;
  if ($[15] !== onChange || $[16] !== trackTagChange || $[17] !== value) {
    t6 = assetIds_0 => {
      const nextValue_1 = {
        ...value,
        collateralExclusion: assetIds_0
      };
      trackTagChange("collateralExclusion", nextValue_1);
      onChange(nextValue_1);
    };
    $[15] = onChange;
    $[16] = trackTagChange;
    $[17] = value;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== allCollateralExclusionAssets || $[20] !== exclusionFilterDefinitions || $[21] !== t6 || $[22] !== value.collateralExclusion) {
    t7 = {
      assets: allCollateralExclusionAssets,
      selectedValues: value.collateralExclusion,
      filterDefinitions: exclusionFilterDefinitions,
      onSelect: t6
    };
    $[19] = allCollateralExclusionAssets;
    $[20] = exclusionFilterDefinitions;
    $[21] = t6;
    $[22] = value.collateralExclusion;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const exclusionTagSlot = useAssetTagSlot(t7);
  let t8;
  if ($[24] !== disabled) {
    t8 = <MasterFilter.Trigger label="Advanced" disabled={disabled} />;
    $[24] = disabled;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <MasterFilter.Toggle name="v1" label="Also show Vaults V1" tooltip="Vaults V2 are shown by default. Turn this on to also show Vaults V1." />;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== collateralExposureItems || $[28] !== exposureTagSlot) {
    t10 = <MasterFilter.AdvancedSelect name="collateralExposure" label="Collateral exposure" items={collateralExposureItems} tooltip="Vaults exposed to these assets will be shown." searchPlaceholder="Search assets..." slot={exposureTagSlot} />;
    $[27] = collateralExposureItems;
    $[28] = exposureTagSlot;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== collateralExclusionItems || $[31] !== exclusionTagSlot) {
    t11 = <MasterFilter.AdvancedSelect name="collateralExclusion" label="Collateral exclusion" items={collateralExclusionItems} tooltip="Vaults exposed to these assets will be hidden." searchPlaceholder="Search assets..." placeholder="None" slot={exclusionTagSlot} />;
    $[30] = collateralExclusionItems;
    $[31] = exclusionTagSlot;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  let t13;
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <MasterFilter.Range name="deposits" label="Deposits" unit="$" />;
    t13 = <MasterFilter.Range name="liquidity" label="Liquidity" unit="$" />;
    t14 = <MasterFilter.Range name="apy" label="APY" unit="%" />;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t12 = $[33];
    t13 = $[34];
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t10 || $[37] !== t11) {
    t15 = <MasterFilter.Content>{t9}{t10}{t11}{t12}{t13}{t14}</MasterFilter.Content>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== onChange || $[40] !== t15 || $[41] !== t8 || $[42] !== tracking.onFieldChange || $[43] !== tracking.onOpenChange || $[44] !== tracking.onReset || $[45] !== value) {
    t16 = <MasterFilter.Provider value={value} onChange={onChange} defaultValue={DEFAULT_MASTER_FILTER_STATE} excludeKeys={VAULTS_MASTER_FILTER_EXCLUDE_KEYS} analyticsExcludeKeys={VAULTS_ANALYTICS_EXCLUDE_KEYS} onOpenChange={tracking.onOpenChange} onReset={tracking.onReset} onFieldChange={tracking.onFieldChange}>{t8}{t15}</MasterFilter.Provider>;
    $[39] = onChange;
    $[40] = t15;
    $[41] = t8;
    $[42] = tracking.onFieldChange;
    $[43] = tracking.onOpenChange;
    $[44] = tracking.onReset;
    $[45] = value;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  return t16;
};
function _temp(asset) {
  return asset.symbol.startsWith("PT-");
}
function _temp2(asset_0) {
  return asset_0.tags?.includes(AssetFilter.STOCKS);
}
function _temp3(assetList) {
  return [...BASE_FILTER_DEFINITIONS, ...(assetList.some(_temp) ? [PT_FILTER_DEFINITION] : []), ...(assetList.some(_temp2) ? [STOCKS_FILTER_DEFINITION] : [])];
}